import { Component, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ConfirmComponent } from "src/app/dashboard/shared-components/confirm/confirm.component";
import { SettingsService } from "src/app/_services/settings.service";
import { ToastrService } from "ngx-toastr";

const DEFAULT_IS_ENABLED = false;
const DEFAULT_PICKUP_ETA_THRESHOLD_MINUTES = 3;
const MIN_PICKUP_ETA_THRESHOLD_MINUTES = 1;
const MAX_PICKUP_ETA_THRESHOLD_MINUTES = 60;

const ASSIGNMENT_STRATEGY_OPTIONS = [
  {
    value: "MONIIC_PRIORITY",
    label: "Phase 1 – Moniic priority",
    help:
      "Uber is used only when no Moniic chauffeur is available, or when Moniic is further than the threshold and Uber can arrive within it.",
  },
  {
    value: "CLOSEST_ETA",
    label: "Phase 2 – Closest vehicle beyond threshold",
    help:
      "Moniic is always preferred within the threshold. Beyond it, the booking goes to whichever vehicle (Moniic or Uber) is closest.",
  },
];
const DEFAULT_ASSIGNMENT_STRATEGY = "MONIIC_PRIORITY";
const PHASE_2_ASSIGNMENT_STRATEGY = "CLOSEST_ETA";

@Component({
  selector: "app-uber-setting",
  templateUrl: "./uber-setting.component.html",
  styleUrls: ["./uber-setting.component.scss"],
})
export class UberSettingComponent implements OnInit {
  readonly minThreshold = MIN_PICKUP_ETA_THRESHOLD_MINUTES;
  readonly maxThreshold = MAX_PICKUP_ETA_THRESHOLD_MINUTES;
  readonly strategyOptions = ASSIGNMENT_STRATEGY_OPTIONS;

  isEnabled = DEFAULT_IS_ENABLED;
  pickupEtaThresholdMinutes: number = DEFAULT_PICKUP_ETA_THRESHOLD_MINUTES;
  assignmentStrategy: string = DEFAULT_ASSIGNMENT_STRATEGY;
  updatedAt: string | null = null;
  isConfigured = false;
  isLoading = false;
  isSaving = false;

  private savedIsEnabled = DEFAULT_IS_ENABLED;
  private savedPickupEtaThresholdMinutes = DEFAULT_PICKUP_ETA_THRESHOLD_MINUTES;
  private savedAssignmentStrategy: string = DEFAULT_ASSIGNMENT_STRATEGY;

  constructor(
    private settingsService: SettingsService,
    private toastrService: ToastrService,
    private modal: NgbModal
  ) {}

  ngOnInit(): void {
    this.loadSetting();
  }

  get isThresholdValid(): boolean {
    const value = Number(this.pickupEtaThresholdMinutes);
    return (
      Number.isInteger(value) &&
      value >= MIN_PICKUP_ETA_THRESHOLD_MINUTES &&
      value <= MAX_PICKUP_ETA_THRESHOLD_MINUTES
    );
  }

  get selectedStrategy() {
    return (
      this.strategyOptions.find(
        (option) => option.value === this.assignmentStrategy
      ) || this.strategyOptions[0]
    );
  }

  get isChanged(): boolean {
    return (
      !this.isConfigured ||
      this.isEnabled !== this.savedIsEnabled ||
      Number(this.pickupEtaThresholdMinutes) !==
        this.savedPickupEtaThresholdMinutes ||
      this.assignmentStrategy !== this.savedAssignmentStrategy
    );
  }

  get canSave(): boolean {
    return (
      !this.isLoading && !this.isSaving && this.isThresholdValid && this.isChanged
    );
  }

  loadSetting(): void {
    this.isLoading = true;
    this.settingsService.getUberSetting().subscribe(
      (res) => {
        this.isLoading = false;
        this.applySetting(res?.result);
      },
      (error) => {
        this.isLoading = false;
        this.applySetting(null);
        this.toastrService.error(
          this.resolveErrorMessage(error, "Failed to load Uber settings"),
          ""
        );
      }
    );
  }

  saveSetting(): void {
    if (!this.canSave) {
      return;
    }

    const isDisabling = this.savedIsEnabled && !this.isEnabled;
    const isSwitchingToPhase2 =
      this.isEnabled &&
      this.savedAssignmentStrategy !== PHASE_2_ASSIGNMENT_STRATEGY &&
      this.assignmentStrategy === PHASE_2_ASSIGNMENT_STRATEGY;
    const threshold = Number(this.pickupEtaThresholdMinutes);
    const summary = `Uber BackFill will be <b>${
      this.isEnabled ? "enabled" : "disabled"
    }</b> with a pickup ETA threshold of <b>${threshold} minute${
      threshold === 1 ? "" : "s"
    }</b> using <b>${this.selectedStrategy.label}</b>.`;

    const modalRef = this.modal.open(ConfirmComponent, {
      scrollable: true,
      windowClass: "success-modal",
    });

    if (isDisabling) {
      modalRef.componentInstance.fromParent = {
        head: "Stop offering new Uber bookings?",
        content: "New bookings will no longer be offered from Uber.",
      };
    } else if (isSwitchingToPhase2) {
      modalRef.componentInstance.fromParent = {
        head: "Switch to Phase 2 assignment?",
        content: `${summary} Bookings beyond the threshold will go to the closest vehicle, which may be Uber.`,
      };
    } else {
      modalRef.componentInstance.fromParent = {
        head: "Save Uber BackFill settings?",
        content: summary,
      };
    }

    modalRef.result.then(
      (result) => {
        if (result && result.isConfirm) {
          this.performSave();
        }
      },
      () => {
        // User cancelled, keep the unsaved values in the form
      }
    );
  }

  private performSave(): void {
    this.isSaving = true;
    this.settingsService
      .updateUberSetting({
        isEnabled: this.isEnabled,
        pickupEtaThresholdMinutes: Number(this.pickupEtaThresholdMinutes),
        assignmentStrategy: this.assignmentStrategy,
      })
      .subscribe(
        (res) => {
          this.isSaving = false;
          this.applySetting(res?.result);
          this.toastrService.success("Uber settings saved", "");
        },
        (error) => {
          this.isSaving = false;
          this.toastrService.error(
            this.resolveErrorMessage(error, "Failed to save Uber settings"),
            ""
          );
        }
      );
  }

  /**
   * The API returns an empty result when no setting has been saved yet.
   */
  private applySetting(setting: any): void {
    const isConfigured =
      !!setting &&
      typeof setting.isEnabled === "boolean" &&
      typeof setting.pickupEtaThresholdMinutes === "number";

    this.isConfigured = isConfigured;
    this.isEnabled = isConfigured ? setting.isEnabled : DEFAULT_IS_ENABLED;
    this.pickupEtaThresholdMinutes = isConfigured
      ? setting.pickupEtaThresholdMinutes
      : DEFAULT_PICKUP_ETA_THRESHOLD_MINUTES;
    this.assignmentStrategy =
      isConfigured &&
      this.strategyOptions.some(
        (option) => option.value === setting.assignmentStrategy
      )
        ? setting.assignmentStrategy
        : DEFAULT_ASSIGNMENT_STRATEGY;
    this.updatedAt = isConfigured ? setting.updatedAt || null : null;
    this.savedIsEnabled = this.isEnabled;
    this.savedPickupEtaThresholdMinutes = this.pickupEtaThresholdMinutes;
    this.savedAssignmentStrategy = this.assignmentStrategy;
  }

  private resolveErrorMessage(error: any, fallback: string): string {
    return error?.error?.message || error?.error?.messageText || fallback;
  }
}
